import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { SharedModule } from "../../../../shared/shared.module";
import { UiFacadeService } from "../../../user_infos/services/store/ui-facade.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { Travail } from "../../../info-journaliere/models/travail.model";
import { format } from "date-fns";

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Jun",
  "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc",
];

@Component({
  selector: "vl-widget-heures-user-infos",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, WidgetPeriodFiltersComponent, SharedModule, LoaderSkeletonComponent],
  templateUrl: "./widget-heures-user-infos.component.html",
  styleUrls: ["./widget-heures-user-infos.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetHeuresUserInfosComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  editable = input(true);

  private uiFacade = inject(UiFacadeService);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  stats = this.uiFacade.getState().stats;

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("heuresChart");
  chart?: Chart<"bar", number[], string>;

  period = signal<string>("m");
  timeScale = signal<string>("MAX");
  comparison = signal<string | null>(null);

  private parseDureeToMinutes(s: unknown): number {
    if (!s) return 0;
    const str = String(s).trim();
    let m = /^(\d{1,3})\s*h\s*(\d{1,2})?$/i.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d{1,3}):(\d{1,2})$/.exec(str);
    if (m) return parseNumber(m[1] || 0) * 60 + parseNumber(m[2] || 0);
    m = /^(\d+(?:[.,]\d+)?)$/.exec(str);
    if (m) return Math.round(parseNumber(m[1].replace(",", ".")) * 60);
    return 0;
  }

  chartData = computed(() => {
    const travail: Travail[] = this.stats()?.travail ?? [];
    const p = this.period();
    const ts = this.timeScale();
    const comp = this.comparison();

    if (!travail.length) return { labels: [], values: [], comparison: [] as number[], comparisonLabels: [] as string[], compMode: comp };

    const grouped = new Map<string, number>();
    for (const t of travail) {
      if (!t.date) continue;
      const key = this.dateKey(new Date(t.date), p);
      grouped.set(key, (grouped.get(key) ?? 0) + this.parseDureeToMinutes(t.duree));
    }

    const entries = [...grouped.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, Math.round((v / 60) * 100) / 100] as [string, number]);

    const filtered = this.filterByScale(entries, ts);
    const labels = filtered.map(([k]) => this.formatLabel(k));
    const values = filtered.map(([, v]) => v);

    if (!comp || ts === "MAX") {
      return { labels, values, comparison: [] as number[], comparisonLabels: [] as string[], compMode: comp };
    }

    // Compute comparison
    const now = new Date();
    const refDate = this.getRefDate(ts);
    let compStart: Date;
    let compEnd: Date;

    if (comp === "n-1") {
      compStart = new Date(refDate);
      compStart.setFullYear(compStart.getFullYear() - 1);
      compEnd = new Date(now);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
    } else {
      const duration = now.getTime() - refDate.getTime();
      compStart = new Date(refDate.getTime() - duration);
      compEnd = new Date(refDate);
    }

    const compGrouped = new Map<string, number>();
    for (const t of travail) {
      if (!t.date) continue;
      const date = new Date(t.date);
      if (date < compStart || date >= compEnd) continue;
      const key = this.dateKey(date, p);
      compGrouped.set(key, (compGrouped.get(key) ?? 0) + this.parseDureeToMinutes(t.duree));
    }

    const compMap = new Map<string, { value: number; key: string }>();
    for (const [key, value] of compGrouped) {
      const relKey = this.relativeKey(key, p);
      compMap.set(relKey, { value: Math.round((value / 60) * 100) / 100, key });
    }

    const compValues = filtered.map(([k]) => compMap.get(this.relativeKey(k, p))?.value ?? 0);
    const comparisonLabels = filtered.map(([k]) => {
      const entry = compMap.get(this.relativeKey(k, p));
      return entry ? this.formatLabel(entry.key) : "";
    });

    return { labels, values, comparison: compValues, comparisonLabels, compMode: comp };
  });

  totalHeures = computed(() => {
    const vals = this.chartData().values;
    return vals.reduce((a, b) => a + b, 0).toFixed(1);
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.destroyChart());

    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private dateKey(d: Date, period: string): string {
    switch (period) {
      case "j":
        return format(d, "yyyy-MM-dd");
      case "s":
        return format(d, "yyyy-'S'ww");
      case "a":
        return format(d, "yyyy");
      case "m":
      default:
        return format(d, "yyyy-MM");
    }
  }

  private formatLabel(key: string): string {
    const m = key.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1].slice(2)}`;
    const s = key.match(/^\d{4}-S(\d{2})$/);
    if (s) return `S${s[1]}`;
    const d = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (d) return `${d[3]}/${d[2]}`;
    return key;
  }

  private getRefDate(scale: string): Date {
    const now = new Date();
    switch (scale) {
      case "1 m":
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case "6 m":
        return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      case "1 a":
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      case "5 a":
        return new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
      case "YTD":
      default:
        return new Date(now.getFullYear(), 0, 1);
    }
  }

  private relativeKey(key: string, period: string): string {
    switch (period) {
      case "m":
        return key.split("-")[1] ?? key;
      case "j":
        return key.substring(5);
      case "s":
        return key.substring(5);
      case "a":
      default:
        return key;
    }
  }

  private filterByScale(entries: [string, number][], scale: string): [string, number][] {
    if (!entries.length) return entries;
    const now = new Date();
    let cutoff: Date;
    switch (scale) {
      case "1 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case "6 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case "1 a":
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case "5 a":
        cutoff = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
        break;
      case "MAX":
        return entries;
      case "YTD":
      default:
        cutoff = new Date(now.getFullYear(), 0, 1);
        break;
    }
    const cutKey = this.dateKey(cutoff, this.period());
    return entries.filter(([k]) => k >= cutKey);
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private ensureHex(color: string): string {
    if (color.startsWith("#")) return color;
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return color;
    ctx.fillStyle = color;
    return ctx.fillStyle;
  }

  private buildChart(data: { labels: string[]; values: number[]; comparison: number[]; comparisonLabels: string[]; compMode: string | null }) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const style = getComputedStyle(this.elementRef.nativeElement);
    const barColor = style.getPropertyValue("--color-on-accent-alt").trim() || "hsl(210 85% 55%)";
    const textColor = style.getPropertyValue("--color-on-background-alt").trim() || "#888";

    const hexColor = this.ensureHex(barColor);
    const hasComparison = data.comparison?.length > 0 && data.comparison.some((v) => v > 0);

    const datasets: any[] = [
      {
        label: "Heures",
        data: data.values,
        backgroundColor: hexColor,
        borderRadius: 4,
      },
    ];

    if (hasComparison) {
      datasets.push({
        label: data.compMode === "n-1" ? "N-1" : "Préc.",
        data: data.comparison,
        backgroundColor: hexColor + "60",
        borderRadius: 4,
      });
    }

    const compLabels = data.comparisonLabels ?? [];
    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: {
        labels: data.labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, maxRotation: 45, font: { size: 10 } },
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 10 },
              callback: (value) => WidgetHelperService.formatNumeric(+value) + "h",
            },
            beginAtZero: true,
          },
        },
        plugins: {
          legend: {
            display: hasComparison,
            position: "bottom",
            labels: { color: textColor, boxWidth: 12, padding: 8, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const idx = items[0]?.dataIndex;
                const label = data.labels[idx] ?? "";
                if (hasComparison && compLabels[idx]) return `${label} vs ${compLabels[idx]}`;
                return label;
              },
              label: (ctx) => ` ${ctx.parsed.y.toFixed(1)}h`,
            },
          },
        },
      },
    };
    this.chart = new Chart<"bar", number[], string>(ctx, config);
  }
}
